// The single URL scheme for the app: `<app-host>/<workspace-slug>/c/<channel>`.
// Workspace = first path segment, channel = the `/c/<name>` suffix. This one
// module is the shared source of truth for the SPA's URL sync, the public
// per-channel pages, and every shareable deep link, so they all agree.
//
// During the subdomain→path migration BOTH forms are understood:
//   /acme/c/database   → path mode (app.ano.dev)
//   /c/database        → legacy subdomain mode (acme.ano.dev; workspace from host)

import {
  validateWorkspaceSlug,
  getWorkspaceSlugFromHostname,
  getWorkspaceSlugFromLocation,
  getWorkspaceBaseDomainFromHostname,
} from "./workspace-validation";

export interface AppRoute {
  /** Workspace slug from the path, or null (home, or legacy subdomain mode). */
  workspaceSlug: string | null;
  /** Channel name, or null when no channel is open. */
  channelName: string | null;
  /**
   * Anchored message id for the public-question deep link
   * (`…/c/<channel>/m/<id>`), or null for every other route. A malformed id
   * degrades to the channel route (null) rather than dead-ending.
   */
  messageId: string | null;
}

// First path segments that are app routes / separators, never a workspace.
const NON_WORKSPACE_SEGMENTS = new Set([
  "c", // the channel separator (`/c/<name>`)
  "callback", // OAuth return
  "integrations", // integration landing pages
  "call-window", // popped-out call window
  "home", // home scope is "/", never "/home"
]);

// Slug group accepts mixed case (browsers preserve path case); we lowercase
// it before the reserved/validity guard so an externally-capitalized link
// (`/Acme/c/db`) resolves to the same workspace as `/acme/c/db`.
// Most-specific first: the `/m/<id>` anchor variants are matched before the
// plain channel patterns so a question deep link isn't swallowed by them.
const WS_CHANNEL_MESSAGE = /^\/([a-zA-Z0-9-]+)\/c\/([^/]+)\/m\/([^/]+)\/?$/; // /acme/c/database/m/<id>
const CHANNEL_MESSAGE_ONLY = /^\/c\/([^/]+)\/m\/([^/]+)\/?$/; //                 /c/database/m/<id> (legacy)
const WS_CHANNEL = /^\/([a-zA-Z0-9-]+)\/c\/([^/]+)\/?$/; // /acme/c/database
const CHANNEL_ONLY = /^\/c\/([^/]+)\/?$/; //               /c/database (legacy)
const WS_ONLY = /^\/([a-zA-Z0-9-]+)\/?$/; //              /acme

// Regex shape copied (NOT imported) from navigation-intent.ts: accept a UUID or
// a safe alphanumeric id, reject anything that could carry an injection.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Validate a message id is a UUID or a safe id, else null (degrade gracefully). */
function validateMessageId(value: string | null): string | null {
  if (!value) return null;
  if (UUID_RE.test(value)) return value;
  if (/^[a-zA-Z0-9_-]{1,128}$/.test(value)) return value;
  return null;
}

function isWorkspaceSegment(seg: string): boolean {
  return !NON_WORKSPACE_SEGMENTS.has(seg) && validateWorkspaceSlug(seg).valid;
}

function safeDecodeURIComponent(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/** Parse the workspace slug + channel name (+ optional message id) from a path. */
export function parseAppPath(
  pathname: string = window.location.pathname,
): AppRoute {
  // /acme/c/database/m/<id> — workspace + channel + anchored message (path mode).
  let m = pathname.match(WS_CHANNEL_MESSAGE);
  if (m) {
    const slug = m[1].toLowerCase();
    const channelName = safeDecodeURIComponent(m[2]);
    if (isWorkspaceSegment(slug) && channelName) {
      return {
        workspaceSlug: slug,
        channelName,
        messageId: validateMessageId(safeDecodeURIComponent(m[3])),
      };
    }
  }
  // /c/database/m/<id> — channel + anchored message (legacy subdomain mode).
  m = pathname.match(CHANNEL_MESSAGE_ONLY);
  if (m) {
    const channelName = safeDecodeURIComponent(m[1]);
    if (channelName) {
      return {
        workspaceSlug: null,
        channelName,
        messageId: validateMessageId(safeDecodeURIComponent(m[2])),
      };
    }
  }
  m = pathname.match(WS_CHANNEL);
  if (m) {
    const slug = m[1].toLowerCase();
    const channelName = safeDecodeURIComponent(m[2]);
    if (isWorkspaceSegment(slug) && channelName)
      return { workspaceSlug: slug, channelName, messageId: null };
  }
  m = pathname.match(CHANNEL_ONLY);
  if (m) {
    return {
      workspaceSlug: null,
      channelName: safeDecodeURIComponent(m[1]),
      messageId: null,
    };
  }
  m = pathname.match(WS_ONLY);
  if (m) {
    const slug = m[1].toLowerCase();
    if (isWorkspaceSegment(slug))
      return { workspaceSlug: slug, channelName: null, messageId: null };
  }
  return { workspaceSlug: null, channelName: null, messageId: null };
}

/** Build the canonical path for a workspace + optional channel. */
export function buildAppPath(
  workspaceSlug?: string | null,
  channelName?: string | null,
): string {
  if (!workspaceSlug) return "/";
  if (!channelName) return `/${workspaceSlug}`;
  return `/${workspaceSlug}/c/${encodeURIComponent(channelName)}`;
}

/**
 * Build the shareable deep link to a single question:
 * `/<slug>/c/<channel>/m/<messageId>`. The slug is assumed already
 * validated/lowercased upstream (same contract as `buildAppPath`).
 */
export function buildPublicQuestionPath(
  workspaceSlug: string,
  channelName: string,
  messageId: string,
): string {
  return `/${workspaceSlug}/c/${encodeURIComponent(
    channelName,
  )}/m/${encodeURIComponent(messageId)}`;
}

type LocationLike = Pick<
  Location,
  "hostname" | "protocol" | "pathname" | "origin"
>;

/**
 * The workspace slug the URL points at — path first (canonical), falling back
 * to the legacy subdomain host during the migration. `"home"` only ever comes
 * from the legacy `home.ano.dev` host (path mode uses `/` for home).
 */
export function workspaceSlugFromUrl(
  loc: LocationLike = window.location,
): string | null {
  const fromPath = parseAppPath(loc.pathname).workspaceSlug;
  if (fromPath) return fromPath;
  return getWorkspaceSlugFromLocation(loc);
}

const ANO_ORIGIN = /^https:\/\/([a-z0-9-]+\.)*ano\.dev$/;

/**
 * The canonical app origin to build links against. On a legacy workspace
 * subdomain (`acme.ano.dev`, `home.ano.dev`) this rewrites the host to
 * `app.<base>`; on the app host / staging it's the current origin.
 *
 * With `{ shareable: true }`, an origin a recipient can't open — Electron
 * (`ano-app://localhost`), localhost, or any non-ano.dev host — falls back to
 * the production app host, while a real `*.ano.dev` origin (incl. staging) is
 * preserved so a staging link never points at prod.
 */
export function appOrigin(
  loc: LocationLike = window.location,
  opts?: { shareable?: boolean },
): string {
  let origin = loc.origin;
  const subdomainSlug = getWorkspaceSlugFromHostname(loc.hostname);
  if (subdomainSlug) {
    const base = getWorkspaceBaseDomainFromHostname(loc.hostname);
    if (base) origin = `${loc.protocol}//app.${base}`;
  }
  if (opts?.shareable && !ANO_ORIGIN.test(origin)) return "https://app.ano.dev";
  return origin;
}
